import { combineLatest, Observable, filter, map } from 'rxjs';
import { Injectable } from '@angular/core';

import { MediaContainer } from '@spartacus/storefront';
import { OccConfig } from '@spartacus/core';

import { DeviceService } from '@seb/web-design-system/core';

import { CustomMedia, ExtendedIMedia, MediaContainerObject } from '../models';
import { TwicpicsService } from './twicpics.service';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  constructor(
    private occConfig: OccConfig,
    private deviceService: DeviceService,
    private twicpicsService: TwicpicsService
  ) {}

  normalizeMedia(media: CustomMedia, preview?: CustomMedia): ExtendedIMedia {
    if (!media) {
      return undefined;
    }
    const isVideo = media.isVideo === 'true' || media.isVideo === true;
    const useTwicPics = !isVideo && this.twicpicsService.isActive;
    const isFallback: boolean = !!media.videoId && !!media.url && media.url !== 'null' && media.url !== '';
    const previewMedia = preview || (isFallback ? { url: media.url } : undefined);
    return {
      ...media,
      isVideo,
      originalUrl: media.url,
      url: this.normalizeImageUrl(media.url),
      urlTwicPic: useTwicPics ? this.twicpicsService.normalizeMediaUrl(media.url) : undefined,
      previewMedia: this.normalizeMedia(previewMedia),
      twicPicsDomain: useTwicPics ? this.twicpicsService.twicpicsDomain : undefined
    };
  }

  getMediaFromContainer(container: MediaContainer): Observable<CustomMedia> {
    const isMobile$ = this.deviceService.isMobile;
    const isTablet$ = this.deviceService.isTablet;
    const isDevice$ = this.deviceService.isDevice;
    return combineLatest([isMobile$, isTablet$, isDevice$]).pipe(
      filter(
        ([isMobile, isTablet, isDevice]) =>
          (isMobile && !isTablet && isDevice) ||
          (!isMobile && isTablet && isDevice) ||
          (!isMobile && !isTablet && !isDevice)
      ),
      map(([isMobile, isTablet]) => {
        if (isMobile) {
          return container['mobile'] || container['tablet'] || container['desktop'];
        } else if (isTablet) {
          return container['tablet'] || container['desktop'] || container['mobile'];
        } else {
          return container['desktop'] || container['tablet'] || container['mobile'];
        }
      })
    );
  }

  // Pour les MediaContainer qui viennent des AdditionalMarketingDatas
  getMediaFromCustomMediaArray(customMedias: CustomMedia[]): CustomMedia {
    const isMobile$ = this.deviceService.isMobile;
    const isTablet$ = this.deviceService.isTablet;
    const isDevice$ = this.deviceService.isDevice;
    combineLatest([isMobile$, isTablet$, isDevice$]).pipe(
      filter(
        ([isMobile, isTablet, isDevice]) =>
          (isMobile && !isTablet && isDevice) ||
          (!isMobile && isTablet && isDevice) ||
          (!isMobile && !isTablet && !isDevice)
      ),
      map(([isMobile, isTablet]) => {
        if (isMobile) {
          return customMedias.find((media) => media.format === 'mobile') || customMedias[0];
        } else if (isTablet) {
          return customMedias.find((media) => media.format === 'tablet') || customMedias[0];
        } else {
          return customMedias.find((media) => media.format === 'desktop') || customMedias[0];
        }
      })
    );
    return customMedias[0];
  }

  getMediaFromMediaContainerObject(container: MediaContainerObject): CustomMedia {
    const isMobile$ = this.deviceService.isMobile;
    const isTablet$ = this.deviceService.isTablet;
    const isDevice$ = this.deviceService.isDevice;

    combineLatest([isMobile$, isTablet$, isDevice$]).pipe(
      filter(
        ([isMobile, isTablet, isDevice]) =>
          (isMobile && !isTablet && isDevice) ||
          (!isMobile && isTablet && isDevice) ||
          (!isMobile && !isTablet && !isDevice)
      ),
      map(([isMobile, isTablet]) => {
        if (isMobile) {
          return container.mobile || container.tablet || container.desktop;
        } else if (isTablet) {
          return container.tablet || container.desktop || container.mobile;
        } else {
          return container.desktop || container.tablet || container.mobile;
        }
      })
    );
    return container.desktop || container.tablet || container.mobile;
  }

  normalizeImageUrl(url: string): string {
    if (new RegExp(/^(http|data:image|\/\/)/i).test(url)) {
      return url;
    }
    return (this.occConfig.backend?.media?.baseUrl || this.occConfig.backend?.occ?.baseUrl || '') + url;
  }
}
